import { useEffect, useState } from 'react';
import { generateUUID } from 'shared/helpers/utils/generate-uuid';
import { AppTableControls } from 'common/TableControls/TableControls';
import { AppMultipleSelect, AppSelect } from 'shared/UI/UIKit/Select';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { getCurriculums } from 'services/api/educationPlans';
import { useGetEducationClassListQuery } from 'shared/api';
import type { Curriculum } from 'services/api/educationPlans';
import type { AppTableColumn } from 'shared/UI/UIKit/Table';
import type { EducationClassRow } from '../types';

type CurriculumOption = { label: string; value: number };

export const useEducationClassesTable = (
  value?: EducationClassRow[],
  onChange?: (rows: EducationClassRow[]) => void,
  readOnly?: boolean,
) => {
  const { data: educationClasses } = useGetEducationClassListQuery({ size: 1000 });

  const [internalDataSource, setInternalDataSource] = useState<EducationClassRow[]>([]);
  const isControlled = value !== undefined && onChange !== undefined;
  const dataSource = isControlled ? value ?? [] : internalDataSource;

  const setDataSource = (updater: (prev: EducationClassRow[]) => EducationClassRow[]) => {
    if (isControlled) {
      onChange(updater(value ?? []));
    } else {
      setInternalDataSource(updater);
    }
  };
  const [curriculumOptions, setCurriculumOptions] = useState<CurriculumOption[]>([]);
  const [curriculumsLoading, setCurriculumsLoading] = useState(false);

  useEffect(() => {
    setCurriculumsLoading(true);
    getCurriculums()
      .then((curriculums: Curriculum[]) => {
        setCurriculumOptions(curriculums.map((c) => ({ label: c.name, value: c?.id ?? 0 })));
      })
      .finally(() => setCurriculumsLoading(false));
  }, []);

  const handleCurriculumChange = (key: string, curriculumId: number | null) => {
    setDataSource((prev) => {
      const next = prev.map((row) => (row.key === key ? { ...row, curriculumId } : row));
      return next;
    });
  };

  const handleEducationClassesChange = (key: string, educationClassIds: number[]) => {
    setDataSource((prev) => {
      const next = prev.map((row) => (row.key === key ? { ...row, educationClassIds } : row));
      return next;
    });
  };

  const selectedCurriculumIds = dataSource
    .map((r) => r.curriculumId)
    .filter((id): id is number => id !== null);

  const selectedEducationClassIds = dataSource
    .map((r) => r.educationClassIds)
    .filter((ids) => ids !== null)
    .flat();

  const handleDeleteRow = (key: string) => {
    setDataSource((prev) => prev.filter((row) => row.key !== key));
  };

  const handleAddRow = () => {
    const newRow: EducationClassRow = {
      key: generateUUID(),
      curriculumId: null,
      educationClassIds: null,
    };
    setDataSource((prev) => [...prev, newRow]);
  };

  const columns: AppTableColumn<EducationClassRow>[] = [
    {
      title: 'Учебный план',
      dataIndex: ['curriculumId'],
      key: 'curriculumId',
      minWidth: 220,
      render: (curriculumValue: number | null, record: EducationClassRow) => (
        <AppSelect
          value={curriculumValue}
          loading={curriculumsLoading}
          showSearch
          optionFilterProp="label"
          options={curriculumOptions.map((opt) => ({
            ...opt,
            disabled: selectedCurriculumIds.includes(opt.value) && opt.value !== curriculumValue,
          }))}
          disabled={readOnly}
          onChange={(curriculumId) => handleCurriculumChange(record.key, curriculumId)}
        />
      ),
    },
    {
      title: 'Классы',
      dataIndex: ['educationClassIds'],
      key: 'educationClassIds',
      minWidth: 220,
      maxWidth: 250,
      render: (educationClassIds: number[] | null, record: EducationClassRow) => (
        <AppMultipleSelect
          value={educationClassIds || []}
          options={
            educationClasses?.content.map((ec) => ({
              label: `${educationParallelTextRepresentation[ec.parallel]}${ec.litera} класс`,
              value: ec.id,
              disabled:
                selectedEducationClassIds.includes(ec.id) && !educationClassIds?.includes(ec.id),
            })) || []
          }
          style={{ height: 'auto', minHeight: '4rem' }}
          disabled={readOnly}
          onChange={(ids) => handleEducationClassesChange(record.key, ids)}
        />
      ),
    },
    {
      key: 'controls',
      title: '',
      dataIndex: [],
      maxWidth: '6rem',
      render: (_: unknown, record: EducationClassRow) => {
        if (!readOnly) {
          return <AppTableControls remove={() => handleDeleteRow(record.key)} />;
        }
        return null;
      },
    },
  ];

  return {
    state: { dataSource, columns },
    methods: { handleAddRow },
  };
};
